import { fetchEventSource } from '@microsoft/fetch-event-source';

/**
 * Open an SSE request and expose its events as an async iterable.
 *
 * Heartbeats and empty-data events are skipped; each remaining event's data
 * is JSON-parsed. Throws after the stream ends if the connection errored.
 *
 * @param {string} url Absolute stream URL.
 * @param {object} [options] Spread into fetchEventSource (method, headers, body, signal, ...);
 *   onopen/onmessage/onerror are managed internally and should not be passed.
 * @yields {{ event: string, data: object }} Parsed SSE events in arrival order.
 */
export async function* streamToSSEEvents(url, options = {}) {
  const queue = [];
  let resolveNext = null;
  let streamComplete = false;
  let streamError = null;

  const notifyNext = () => {
    if (resolveNext) {
      resolveNext();
      resolveNext = null;
    }
  };

  const streamPromise = fetchEventSource(url, {
    ...options,
    openWhenHidden: true,
    onopen: async (response) => {
      if (!response.ok) {
        // Guardrail/validation failures (e.g. 422) respond before the stream starts,
        // with a user-facing message in `detail` — surface it instead of a generic error.
        let detail;
        try {
          const body = await response.json();
          detail = body?.detail;
        } catch {
          // response body wasn't JSON; fall back to the generic message below
        }
        const error = new Error(detail || `Stream request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
      }
    },
    onmessage: (message) => {
      try {
        if (!message.data?.trim()) return; // skip heartbeats and empty-data events
        const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
        queue.push({ event: message.event, data });
        notifyNext();
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    },
    onerror: (error) => {
      streamError = error;
      streamComplete = true;
      notifyNext();
      throw error;
    },
  })
    .catch((error) => {
      streamError = error;
    })
    .finally(() => {
      streamComplete = true;
      notifyNext();
    });

  while (!streamComplete || queue.length > 0) {
    if (queue.length === 0 && !streamComplete) {
      await new Promise((resolve) => {
        resolveNext = resolve;
      });
    }

    if (queue.length > 0) {
      yield queue.shift();
    }
  }

  await streamPromise;

  if (streamError) {
    throw streamError;
  }
}
